import React from "react";
import { Check, ArrowRight, ShieldCheck, HelpCircle, Layers } from "lucide-react";
import { getAllCompareArticles, CompareArticle } from "../../utils/compareRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";

interface CompareHubPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function CompareHubPage({ onNavigate, onLogin = () => {}, currentUser }: CompareHubPageProps) {
  const compares = getAllCompareArticles();

  useSeoMetadata({
    title: "BizNearYou Comparisons & Search Methodologies",
    description: "Objective comparisons between BizNearYou local business discovery, traditional job boards, and direct employer outreach strategies.",
    canonicalUrl: "https://getbiznearyou.com/compare",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Local Job Search Method Comparison Directory",
      "itemListElement": compares.map((c, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": c.h1,
        "url": `https://getbiznearyou.com/compare/${c.slug}`
      }))
    }
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs items={[{ label: "Compare", path: "/compare" }]} onNavigate={onNavigate} />

        {/* Hero Banner */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 mb-10 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-3xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              <Layers className="w-3.5 h-3.5" />
              <span>Objective Method Comparisons</span>
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-tight">
              Comparing Local Business Search Strategies
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Discover how direct local business discovery compares to traditional ad-based job portals, corporate ATS web forms, and generic resume blasts.
            </p>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {compares.map((comp) => (
            <div
              key={comp.slug}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition flex flex-col justify-between"
            >
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white mb-3 leading-snug">
                  <a
                    href={`/compare/${comp.slug}`}
                    onClick={(e) => handleLinkClick(e, `/compare/${comp.slug}`)}
                    className="hover:text-indigo-600 dark:hover:text-indigo-400 transition cursor-pointer"
                  >
                    {comp.h1}
                  </a>
                </h2>

                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                  {comp.summary}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                  Neutral Benchmark
                </span>
                <a
                  href={`/compare/${comp.slug}`}
                  onClick={(e) => handleLinkClick(e, `/compare/${comp.slug}`)}
                  className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 cursor-pointer"
                >
                  <span>Read Breakdown</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <SeoRelatedLinks currentRoute="/compare" onNavigate={onNavigate} />
      </main>
    </div>
  );
}
